import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  CubeIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ReceiptPercentIcon,
  InformationCircleIcon,
  SparklesIcon,
  BoltIcon,
  CpuChipIcon,
  TagIcon,
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  RectangleStackIcon,
  BuildingOffice2Icon,
  CloudArrowDownIcon,
  ChevronDownIcon,
  CheckIcon,
  ChevronRightIcon,
  CogIcon,
  PlusIcon,
  EyeSlashIcon,
  CalendarIcon,
  Squares2X2Icon,
  BugAntIcon,
  CreditCardIcon,
  BookmarkIcon,
  BookmarkSlashIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import Logo from '../icons/Logo';
import crmLogoBlack from '../../assets/crm_logo_new_black.svg';
import crmLogoWhite from '../../assets/crm_logo_new_white.svg';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useFAB } from '../../contexts/FABContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CommandPalette, CommandPaletteItem } from '../common/CommandPalette';
import { useOrganizations } from '../../hooks/useOrganizations';
import NotificationButton from '../common/NotificationButton';
import ThemeSelector from '../common/ThemeSelector';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { userService } from '../../services/userService';

// Define navigation item interface
interface NavigationItem {
  name: string;
  href?: string;
  icon: React.FC<React.ComponentProps<'svg'>>;
  roles?: string[];
  disabled?: boolean;
  beta?: boolean;
  group?: string;
  module?: string; // Module permission key
  children?: NavigationItem[];
  isGroup?: boolean;
}

// Adjunk hozzá egy interface-t a NavBar props-jaihoz
interface NavBarProps {
  systemMessage?: string;
}

const navigation: NavigationItem[] = [
  { name: 'nav.dashboard', href: '/', icon: HomeIcon },
  
  // CRM & Contacts Group
  {
    name: 'nav.groups.crm',
    icon: UserGroupIcon,
    isGroup: true,
    children: [
  { name: 'nav.customers', href: '/customers', icon: UserGroupIcon, module: 'customers' },
      { name: 'nav.customerGroups', href: '/customer-groups', icon: UserGroupIcon, module: 'customers' },
      { name: 'nav.leads', href: '/leads', icon: SparklesIcon, module: 'customers' },
      { name: 'nav.suppliers', href: '/suppliers', icon: BuildingStorefrontIcon, module: 'suppliers' },
  { name: 'nav.duplicateScanner', href: '/duplicate-scanner', icon: MagnifyingGlassIcon, module: 'customers' },
      { name: 'nav.notes', href: '/notes', icon: DocumentTextIcon, module: 'notes' },
      { name: 'nav.richNotes', href: '/rich-notes', icon: ClipboardDocumentListIcon, module: 'notes' },
      { name: 'nav.appointments', href: '/appointments', icon: CalendarIcon, module: 'appointments' },
    ]
  },

  // Customer Portal Group - commented out for community version
  // {
  //   name: 'nav.groups.customerPortal',
  //   icon: GlobeAltIcon,
  //   isGroup: true,
  //   children: [
  //     { name: 'nav.portalPreview', href: '/customer-portal', icon: GlobeAltIcon, module: 'customerPortal' },
  //     { name: 'nav.missingItems', href: '/missing-items', icon: ExclamationTriangleIcon, module: 'customerPortal' },
  //     { name: 'nav.customerProjects', href: '/customer-tickets', icon: ClipboardDocumentListIcon, module: 'customerPortal' },
  //   ]
  // },

  // Products & Inventory Group
  {
    name: 'nav.groups.products',
    icon: CubeIcon,
    isGroup: true,
    children: [
  { name: 'nav.products', href: '/products', icon: CubeIcon, module: 'products' },
  { name: 'nav.categories', href: '/categories', icon: TagIcon, module: 'products' }, 
      { name: 'nav.warehouse', href: '/warehouse', icon: BuildingStorefrontIcon, module: 'inventory' },
      { name: 'nav.inventory', href: '/inventory', icon: ClipboardDocumentListIcon, module: 'inventory' },
      // { name: 'nav.warranty', href: '/warranty', icon: ShieldExclamationIcon, module: 'products' },
    ]
  },

  // Sales & Orders Group
  {
    name: 'nav.groups.sales',
    icon: ShoppingCartIcon,
    isGroup: true,
    children: [
  { name: 'nav.pipeline', href: '/pipeline', icon: ChartBarIcon, module: 'quotations' },
  { name: 'nav.offers', href: '/quotations', icon: DocumentTextIcon, module: 'quotations' },
  { name: 'nav.ai_quotations', href: '/ai-quotations', icon: SparklesIcon, module: 'quotations' },
  { name: 'nav.orders', href: '/orders', icon: ShoppingCartIcon, module: 'quotations' },
  { name: 'nav.delivery_notes', href: '/delivery-notes', icon: TruckIcon, module: 'quotations' },
  { name: 'nav.sales', href: '/sales', icon: ShoppingCartIcon, module: 'sales' },
  { name: 'nav.deals', href: '/deals', icon: CurrencyDollarIcon, module: 'deals' },
  { name: 'nav.campaigns', href: '/campaigns', icon: EnvelopeIcon, module: 'customers' },
    ]
  },

  // Documents Group
  {
    name: 'nav.groups.documents',
    icon: DocumentTextIcon,
    isGroup: true,
    children: [
      { name: 'nav.documentLibrary', href: '/document-storage', icon: FolderIcon, module: 'customers' },
      { name: 'nav.templates', href: '/templates', icon: DocumentTextIcon, module: 'templates' },
      { name: 'nav.documentVariables', href: '/document-variables', icon: CogIcon, module: 'templates' },
    ]
  },

  // Contracts Group - Elrejtve, mert a dokumentumtár kezeli
  // {
  //   name: 'nav.groups.contracts',
  //   icon: DocumentTextIcon,
  //   isGroup: true,
  //   children: [
  //     { name: 'nav.contracts', href: '/contracts', icon: DocumentTextIcon, module: 'contracts' },
  //   ]
  // },

  // Forms Group
  {
    name: 'nav.groups.forms',
    icon: RectangleStackIcon,
    isGroup: true,
    children: [
      { name: 'nav.formBuilder', href: '/form-builder', icon: RectangleStackIcon, module: 'forms' },
      { name: 'nav.formBuilderEditor', href: '/form-builder-editor', icon: RectangleStackIcon, module: 'forms' },
    ]
  },

  // Processes Group
  {
    name: 'nav.groups.processes',
    icon: CogIcon,
    isGroup: true,
    children: [
      { name: 'nav.flowBuilder', href: '/flow-builder', icon: CogIcon, module: 'forms' },
      { name: 'nav.flowDebug', href: '/flow-debug', icon: BugAntIcon, module: 'forms' },
    ]
  },

  // Project Management Group
  {
    name: 'nav.groups.projects',
    icon: ChartBarIcon,
    isGroup: true,
    children: [
      { name: 'nav.projectBoard', href: '/project-board', icon: Squares2X2Icon, module: 'projects' },
      { name: 'nav.projects', href: '/projects', icon: ChartBarIcon, module: 'projects' },
      { name: 'nav.projectReports', href: '/project-reports', icon: ChartBarIcon, module: 'projects' },
      { name: 'nav.tasks', href: '/tasks', icon: ClipboardDocumentListIcon, module: 'tasks' },
    ]
  },

  // Worksheets - Top level menu
  {
    name: 'nav.groups.worksheets',
    icon: ClipboardDocumentListIcon,
    isGroup: true,
    children: [
      { name: 'nav.worksheets', href: '/worksheets', icon: ClipboardDocumentListIcon, module: 'worksheets' },
      { name: 'nav.worksheetTemplates', href: '/worksheet-templates', icon: RectangleStackIcon, module: 'worksheets' },
      { name: 'nav.worksheetSettings', href: '/worksheet-settings', icon: Cog6ToothIcon, module: 'worksheets' },
    ]
  },

  // Finance Group
  {
    name: 'nav.groups.finance',
    icon: ReceiptPercentIcon,
    isGroup: true,
    children: [
      { name: 'nav.billing', href: '/billing', icon: ReceiptPercentIcon, module: 'billing' },
      { name: 'nav.reports', href: '/reports', icon: ChartBarIcon, module: 'reports' },
      { name: 'nav.subscriptions', href: '/subscriptions', icon: CreditCardIcon, module: 'subscriptions' },
    ]
  },

  // Settings & Admin
  { name: 'nav.settings', href: '/settings', icon: Cog6ToothIcon, module: 'settings' },
  {
    name: 'nav.system_admin',
    href: '/system-admin',
    icon: ShieldCheckIcon,
    roles: ['super_admin', 'admin'],
    module: 'admin',
    children: [
      {
        name: 'nav.storageOptimization',
        href: '/storage-optimization',
        icon: CloudArrowDownIcon,
        roles: ['super_admin'],
        module: 'admin'
      }
    ]
  },
];

const NavBar = ({ systemMessage }: NavBarProps) => {
  const { user, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { theme } = useTheme();
  const { isSidebarCollapsed, toggleSidebar } = useSidebar();
  const { isFABVisible, toggleFAB } = useFAB();
  const { canAccess, isSuperAdmin, permissions } = usePermissions();
  const { organizations, loading: orgsLoading } = useOrganizations();
  const location = useLocation();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileOrgOpen, setIsMobileOrgOpen] = useState(false);
  const [isOrgMenuOpen, setIsOrgMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isLogoVisible, setIsLogoVisible] = useState(false);
  const [isTabletSize, setIsTabletSize] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [hoverMenuPosition, setHoverMenuPosition] = useState({ top: 0, left: 0 });
  const [showOrgSelector, setShowOrgSelector] = useState(false);
  const [orgSelectorPosition, setOrgSelectorPosition] = useState({ top: 0, left: 0 });
  const [pinnedItems, setPinnedItems] = useState<string[]>(() => {
    // Először a user dokumentumból töltjük be, ha van
    if (user?.pinnedNavItems) {
      return user.pinnedNavItems;
    }
    // Ha nincs, localStorage fallback
    try {
      const saved = localStorage.getItem('youcrm_pinned_nav');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Ha a user dokumentumban változik a pinnedNavItems, szinkronizáljuk
  useEffect(() => {
    if (user?.pinnedNavItems) {
      setPinnedItems(user.pinnedNavItems);
    }
  }, [user?.id]);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const orgTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const orgMenuRef = useRef<HTMLDivElement>(null);

  // Navigáció kezelő - megakadályozza az újratöltést, ha ugyanazon az oldalon vagyunk
  const handleNavClick = (e: React.MouseEvent, href: string) => {
    if (location.pathname === href) {
      e.preventDefault();
      return;
    }
  };

  const filteredNavigation = navigation.filter(item => {
    // Customer szerepkör csak az ügyfélportált látja
    if (user?.role === 'customer') {
      // Csak az ügyfélportál menüpont
      if (item.href === '/customer-portal' && item.module === 'customerPortal') return true;
      
      // Csoportok esetén ellenőrizzük a gyerekeket
      if (item.isGroup && item.children) {
        const filteredChildren = item.children.filter(child => 
          child.href === '/customer-portal' && child.module === 'customerPortal'
        );
        if (filteredChildren.length > 0) {
          item.children = filteredChildren;
          return true;
        }
      }
      
      return false;
    }
    
    // Ha nincs szervezet rendelve (kivéve super_admin), csak a dashboard és profil beállítások láthatók
    if (!user?.organizationId && user?.role !== 'super_admin') {
      return item.href === '/' || item.href === '/profile' || (item.module === 'settings' && item.href === '/settings');
    }
    
    // Dashboard mindig látható (kivéve customer)
    if (item.href === '/') return true;
    
    // Szerepkör alapú szűrés (super_admin elemek)
    if (item.roles && !item.roles.includes(user?.role || '')) {
      return false;
    }
    
    // Csoportok esetén ellenőrizzük a gyerek elemeket
    if (item.isGroup && item.children) {
      return item.children.some(child => {
        if (child.module) {
          return canAccess(child.module as keyof import('../../types/user').UserModulePermissions);
        }
        return true;
      });
    }
    
    // Modul alapú jogosultság ellenőrzés
    if (item.module) {
      return canAccess(item.module as keyof import('../../types/user').UserModulePermissions);
    }
    
    return true;
  });

  // Flat menüpontok a Command Palette-hez (jogosultsági szűréssel)
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const flatNavItems = useMemo((): CommandPaletteItem[] => {
    const items: CommandPaletteItem[] = [];
    for (const item of filteredNavigation) {
      if (item.isGroup && item.children) {
        for (const child of item.children) {
          if (child.href && !child.disabled) {
            items.push({
              name: child.name,
              href: child.href,
              icon: child.icon,
              groupName: item.name
            });
          }
        }
      } else if (item.href && !item.disabled) {
        items.push({
          name: item.name,
          href: item.href,
          icon: item.icon
        });
      }
    }
    return items;
  }, [filteredNavigation]);

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen(prev => !prev);
      }
    };
    const handleOpen = () => setIsCommandPaletteOpen(true);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('openCommandPalette', handleOpen);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('openCommandPalette', handleOpen);
    };
  }, []);

  // Check if a navigation item is active based on the current path
  const isActiveRoute = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    // Special case: /projects-gantt should highlight /project-board
    if (href === '/project-board' && location.pathname === '/projects-gantt') {
      return true;
    }
    // Exact match or starts with href followed by / or query params
    return location.pathname === href || 
           (location.pathname.startsWith(href + '/')) ||
           (location.pathname.startsWith(href + '?'));
  };

  // Meghatározza a modul státuszát vizuális jelzéshez
  const getModuleStatus = (item: NavigationItem) => {
    // Dashboard és nem modul elemek alapértelmezetten engedélyezettek
    if (!item.module || item.href === '/') {
      return 'enabled';
    }

    // Közös modulok (settings, admin) nem jelennek meg jelöléssel - mindenki számára elérhetők
    if (item.module === 'settings' || item.module === 'admin') {
      return 'common'; // Új státusz a közös moduloknak
    }

    // Super admin minden modulhoz hozzáfér
    if (isSuperAdmin) {
      return 'enabled';
    }

    // Modul jogosultság ellenőrzése
    const modulePermission = permissions[item.module as keyof typeof permissions];
    
    if (modulePermission === 'none') {
      return 'disabled';
    } else if (modulePermission === 'access') {
      return 'enabled';
    } else if (modulePermission === 'admin') {
      return 'premium'; // Admin jogosultság = prémium funkció
    }
    
    return 'disabled';
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(prev => !prev);
  };

  const handleLanguageChange = (lang: 'hu' | 'en') => {
    setLanguage(lang);
  };

  const handleOrganizationChange = async (orgId: string) => {
    if (!user) return;
    
    try {
      await userService.updateUser(user.id, { organizationId: orgId });
      setIsOrgMenuOpen(false);
      // Refresh the page to update the context
      window.location.reload();
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  const getCurrentOrganization = () => {
    if (!user?.organizationId) return null;
    return organizations.find(org => org.id === user.organizationId);
  };

  // A vezérlőpult a nyitóoldal – nincs értelme kedvencekbe tenni
  const isPinnable = (href?: string) => !!href && href !== '/' && !href.startsWith('/dashboard') && !href.startsWith('/modern-dashboard');

  const togglePin = async (href: string) => {
    if (!isPinnable(href)) return;
    setPinnedItems(prev => {
      const next = prev.includes(href) ? prev.filter(h => h !== href) : [...prev, href];
      // Mindig mentjük localStorage-ba is (fallback)
      localStorage.setItem('youcrm_pinned_nav', JSON.stringify(next));
      // És Firestore-ba, ha van bejelentkezett user
      if (user?.id) {
        updateDoc(doc(db, 'users', user.id), {
          pinnedNavItems: next
        }).catch(err => console.error('Error saving pinned items:', err));
      }
      return next;
    });
  };

  const isPinned = (href: string) => isPinnable(href) && pinnedItems.includes(href);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  const isGroupExpanded = (groupName: string) => {
    return expandedGroups.has(groupName);
  };

  const handleGroupHover = (groupName: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isSidebarCollapsed) return;
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverMenuPosition({
      top: rect.top,
      left: rect.right + 8
    });
    setHoveredGroup(groupName);
  };

  const handleGroupLeave = () => {
    // Delay hiding the menu to allow mouse movement to the menu
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredGroup(null);
    }, 150);
  };

  const handleMenuEnter = () => {
    // Clear timeout when mouse enters the menu
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleMenuLeave = () => {
    // Immediately hide when leaving the menu
    setHoveredGroup(null);
  };

  const handleOrgHover = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!isSidebarCollapsed) return;
    
    // Clear any existing timeout
    if (orgTimeoutRef.current) {
      clearTimeout(orgTimeoutRef.current);
      orgTimeoutRef.current = null;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    setOrgSelectorPosition({
      top: rect.top,
      left: rect.right + 8
    });
    setShowOrgSelector(true);
  };

  const handleOrgLeave = () => {
    // Delay hiding the org selector
    orgTimeoutRef.current = setTimeout(() => {
      setShowOrgSelector(false);
    }, 150);
  };

  const handleOrgMenuEnter = () => {
    // Clear timeout when mouse enters the org menu
    if (orgTimeoutRef.current) {
      clearTimeout(orgTimeoutRef.current);
      orgTimeoutRef.current = null;
    }
  };

  const handleOrgMenuLeave = () => {
    // Immediately hide when leaving the org menu
    setShowOrgSelector(false);
  };

  const hasActiveChild = (item: NavigationItem): boolean => {
    if (item.children) {
      return item.children.some(child => child.href && isActiveRoute(child.href));
    }
    return false;
  };

  // Kattintás figyelése a menüken kívül
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target as Node)) {
        setIsOrgMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Ablakméret figyelése
  useEffect(() => {
    // Inicializáljuk a jelenlegi mérettel
    const width = window.innerWidth;
    setIsLogoVisible(width >= 768);
    setIsTabletSize(width >= 768 && width < 1024);

    // Eseménykezelő az ablakméret változásához
    const handleResize = () => {
      const width = window.innerWidth;
      setIsLogoVisible(width >= 768);
      setIsTabletSize(width >= 768 && width < 1024);
    };

    // Feliratkozás a resize eseményre
    window.addEventListener('resize', handleResize);

    // Leiratkozás a komponens unmountoláskor
    return () => {
      window.removeEventListener('resize', handleResize);
      // Clear hover timeouts on unmount
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (orgTimeoutRef.current) {
        clearTimeout(orgTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Asztali nézet - oldalsáv */}
      <div className={`h-full bg-[var(--crm-surface)] border-r border-[var(--crm-border)] hidden md:flex md:flex-col fixed inset-y-0 left-0 z-50 transition-all duration-300 ${
        isSidebarCollapsed ? 'w-16' : isTabletSize ? 'w-48' : 'w-64'
      }`}>
        {/* Header area - minimal border */}
        <div className="flex-shrink-0 border-b border-gray-200/50 dark:border-gray-700/50 h-2"></div>

        {/* Collapsed Organization Icon with Logo */}
        {organizations.length > 0 && isSidebarCollapsed && (
          <div className="px-3 mb-4 mt-4">
            <button
              onMouseEnter={handleOrgHover}
              onMouseLeave={handleOrgLeave}
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
              title={getCurrentOrganization()?.name || t('nav.selectOrganization')}
            >
              <div className="w-9 h-9 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {getCurrentOrganization()?.name?.charAt(0).toUpperCase() || '?'}
                </span>
              </div>
            </button>
          </div>
        )}

        {/* Organization Changer - Modern dropdown */}
        {organizations.length > 0 && !isSidebarCollapsed && (
          <div className="px-3 mb-4" ref={orgMenuRef}>
            <div className="relative">
              <button
                onClick={() => setIsOrgMenuOpen(!isOrgMenuOpen)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-[var(--crm-border)] hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group"
              >
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <img 
                    src={theme === 'dark' ? crmLogoWhite : crmLogoBlack} 
                    alt="YouCRM Logo" 
                    className="h-6 w-auto flex-shrink-0" 
                  />
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate leading-tight" title={getCurrentOrganization()?.name}>
                      {getCurrentOrganization()?.name || t('nav.selectOrganization')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('nav.organization')}
                    </p>
              </div>
                </div>
                <ChevronDownIcon className={`h-4 w-4 self-center transition-transform duration-200 ${
                  isOrgMenuOpen ? 'rotate-180' : ''
                } text-gray-400`} />
              </button>

              {/* Dropdown Menu */}
              {isOrgMenuOpen && (
                <div className="absolute top-full mt-2 left-0 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden z-50">
                  <div className="py-2 max-h-64 overflow-y-auto">
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleOrganizationChange(org.id)}
                        className={`w-full flex items-start justify-between px-4 py-3 min-h-[60px] text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          user?.organizationId === org.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className={`p-1.5 rounded-lg ${
                            user?.organizationId === org.id 
                              ? 'bg-primary-200/50 dark:bg-primary-800/30' 
                              : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            <BuildingOffice2Icon className={`h-3.5 w-3.5 ${
                              user?.organizationId === org.id 
                                ? 'text-primary-600 dark:text-primary-400' 
                                : 'text-gray-500 dark:text-gray-400'
                            }`} />
                          </div>
                          <div className="min-w-0 flex-1 text-left">
                            <p className={`font-medium break-words leading-tight ${
                              user?.organizationId === org.id 
                                ? 'text-primary-700 dark:text-primary-300' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {org.name}
                            </p>
                            {org.taxNumber && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                {org.taxNumber}
                              </p>
                            )}
        </div>
                        </div>
                        {user?.organizationId === org.id && (
                          <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* A teljes maradék terület fix elrendezése */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Navigáció - maximum magasság és scroll, ha túl hosszú */}
          <nav className="flex-1 px-3 pt-2 pb-3 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300/60 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-400/60">
            <div className="space-y-0.5">
              {/* Kedvencek szekció */}
              {pinnedItems.length > 0 && !location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/modern-dashboard') && (
                <div className={`${isSidebarCollapsed ? '' : 'mb-3 pb-2 border-b border-gray-200/60 dark:border-gray-700/50'}`}>
                  {!isSidebarCollapsed && (
                    <div className="px-2.5 py-1 mb-1">
                      <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        {t('nav.favorites')}
                      </span>
                    </div>
                  )}
                  {flatNavItems
                    .filter(navItem => isPinnable(navItem.href) && pinnedItems.includes(navItem.href))
                    .map(navItem => {
                      const active = isActiveRoute(navItem.href);
                      return (
                        <Link
                          key={`pinned-${navItem.href}`}
                          to={navItem.href}
                          onClick={(e) => handleNavClick(e, navItem.href)}
                          title={isSidebarCollapsed ? t(navItem.name) : undefined}
                          className={`group relative flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-2' : 'px-2.5 py-1.5'} text-sm font-medium rounded-lg transition-all duration-200 tracking-tight mb-0.5 ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                          }`}
                        >
                          <div className={`relative p-1 rounded-md ${active ? 'bg-primary-100 dark:bg-primary-500/20' : 'bg-transparent'}`}>
                            <navItem.icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-primary-600 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'}`} />
                          </div>
                          {!isSidebarCollapsed && (
                            <>
                              <span className="ml-2.5 truncate flex-1">{t(navItem.name)}</span>
                              <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(navItem.href); }}
                                className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                                title={t('nav.unpin')}
                              >
                                <BookmarkSlashIcon className="h-3.5 w-3.5 text-gray-400" />
                              </button>
                            </>
                          )}
                        </Link>
                      );
                    })}
                </div>
              )}
              {filteredNavigation.map((item, index) => {
                const showSeparator = index > 0 && item.isGroup;
                // Handle groups
                if (item.isGroup && item.children) {
                  const hasActiveChild = item.children.some(child => child.href && isActiveRoute(child.href));
                  const isExpanded = isGroupExpanded(item.name);
                  
                  return (
                    <div key={item.name} className="space-y-0.5">
                      {showSeparator && (
                        <div className="my-0.5 mx-1 border-t border-gray-200/60 dark:border-gray-700/50" />
                      )}
                      {/* Group Header */}
                      <div className="relative">
                        <button
                          onClick={() => !isSidebarCollapsed && toggleGroup(item.name)}
                          onMouseEnter={(e) => handleGroupHover(item.name, e)}
                          onMouseLeave={handleGroupLeave}
                          className={`w-full group relative flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-2' : 'px-2.5 py-1.5'} text-sm font-medium rounded-lg transition-all duration-200 tracking-tight ${
                            hasActiveChild
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                          }`}
                          title={isSidebarCollapsed ? t(item.name) : undefined}
                        >
                          <div className={`relative p-1 rounded-md transition-all duration-200 ${
                            hasActiveChild 
                              ? 'bg-transparent'
                              : 'bg-transparent'
                          }`}>
                            <item.icon className={`h-4 w-4 flex-shrink-0 transition-all duration-200 ${
                              hasActiveChild 
                                ? 'text-primary-600 dark:text-primary-400'
                                : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                            }`} />
                          </div>
                          
                          {!isSidebarCollapsed && (
                            <>
                              <span className="ml-2.5 truncate flex-1 text-left">{t(item.name)}</span>
                              
                              <ChevronRightIcon className={`h-4 w-4 transition-transform duration-200 ${
                                isExpanded ? 'rotate-90' : ''
                              } ${hasActiveChild ? 'text-primary-500' : 'text-gray-400'}`} />
                            </>
                          )}
                        </button>

                      </div>

                      {/* Group Children */}
                      {isExpanded && !isSidebarCollapsed && (
                        <div className="ml-4 space-y-0 border-l border-gray-200 dark:border-gray-700 pl-3">
                          {item.children.map((child) => {
                            if (!child.href) return null;
                            
                            const active = isActiveRoute(child.href);
                            const moduleStatus = getModuleStatus(child);
                            
                            if (child.disabled) {
                              return (
                                <div
                                  key={child.name}
                                  className="flex items-center px-2.5 py-1.5 text-sm font-medium rounded-lg text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                >
                                  <div className="relative p-1 rounded-md bg-gray-100/50">
                                    <child.icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-500" />
                                  </div>
                                  <span className="ml-2.5 truncate">{t(child.name)}</span>
                                </div>
                              );
                            }
                            
                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                onClick={(e) => child.href && handleNavClick(e, child.href)}
                                className={`group relative flex items-center px-2.5 py-1 text-sm font-medium rounded-lg transition-all duration-200 tracking-tight ${
                                  active 
                                    ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300 font-medium'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                                }`}
                              >
                                {active && (
                                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary-500 rounded-r-full"></div>
                                )}
                                
                                <div className={`relative p-1 rounded-md transition-all duration-200 ${
                                  active 
                                    ? 'bg-primary-500/15 shadow-sm'
                                    : 'group-hover:bg-primary-500/5'
                                }`}>
                                  <child.icon className={`h-3.5 w-3.5 flex-shrink-0 transition-all duration-200 ${
                                    active 
                                      ? 'text-primary-600 dark:text-primary-400'
                                      : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                                  }`} />
                                </div>
                                
                                <span className="ml-2.5 truncate flex-1">{t(child.name)}</span>
                                
                                {/* Module status indicator */}
                                {child.module && moduleStatus !== 'common' && (
                                  <div className="ml-auto flex-shrink-0">
                                    {moduleStatus === 'enabled' && (
                                      <></>
                                    )}
                                    {moduleStatus === 'premium' && (
                                      <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" title={t('common.modules.premium')}></div>
                                    )}
                                    {moduleStatus === 'disabled' && (
                                      <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-600 rounded-full" title={t('common.modules.disabled')}></div>
                                    )}
                                  </div>
                                )}
                                
                                {/* Pin button */}
                                {child.href && !child.href.startsWith('/dashboard') && !child.href.startsWith('/modern-dashboard') && (
                                  <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(child.href!); }}
                                    className={`ml-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isPinned(child.href!) ? 'opacity-100' : ''}`}
                                    title={isPinned(child.href!) ? t('nav.unpin') : t('nav.pin')}
                                  >
                                    {isPinned(child.href!) ? (
                                      <BookmarkSlashIcon className="h-3.5 w-3.5 text-gray-400" />
                                    ) : (
                                      <BookmarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-amber-500" />
                                    )}
                                  </button>
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // Handle regular items (non-group)
                if (!item.isGroup && item.href) {
                const active = isActiveRoute(item.href);
                const moduleStatus = getModuleStatus(item);
                
                if (item.disabled) {
                  return (
                    <div
                      key={item.name}
                      className={`flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-3' : 'px-2.5 py-1.5'} text-sm font-medium rounded-lg text-gray-400 dark:text-gray-500 cursor-not-allowed`}
                      title={isSidebarCollapsed ? t(item.name) : undefined}
                    >
                      <div className="relative p-1 rounded-md bg-gray-100/50">
                        <item.icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-500" />
                      </div>
                      {!isSidebarCollapsed && (
                        <span className="ml-2.5 truncate">{t(item.name)}</span>
                      )}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={(e) => item.href && handleNavClick(e, item.href)}
                    className={`group relative flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-3' : 'px-2.5 py-1.5'} text-sm font-medium rounded-lg transition-all duration-200 tracking-tight ${
                      active 
                        ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
                    }`}
                    title={isSidebarCollapsed ? t(item.name) : undefined}
                  >
                    {active && (
                      <>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-600 dark:bg-primary-400 rounded-r-full"></div>
                        
                      </>
                    )}
                    
                    <div className={`relative p-1 rounded-md transition-all duration-200 ${
                      active 
                        ? 'bg-primary-500/15 shadow-sm ring-1 ring-primary-500/20'
                        : 'group-hover:bg-primary-500/5'
                    }`}>
                      <item.icon className={`h-3.5 w-3.5 flex-shrink-0 transition-all duration-200 ${
                        active 
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                      }`} />
                    </div>
                    
                    {!isSidebarCollapsed && (
                      <>
                        <span className="ml-2.5 truncate transition-all duration-200 group-hover:translate-x-0.5">{t(item.name)}</span>
                        
                        {item.module && moduleStatus !== 'common' && (
                          <div className="ml-auto flex-shrink-0">
                            {moduleStatus === 'enabled' && (
                              <></>
                            )}
                            {moduleStatus === 'premium' && (
                              <div className="w-2 h-2 bg-amber-400 rounded-full" title={t('common.modules.premium')}></div>
                            )}
                            {moduleStatus === 'disabled' && (
                              <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full shadow-sm" title={t('common.modules.disabled')}></div>
                            )}
                          </div>
                        )}
                        
                        {/* Pin button */}
                        {isPinnable(item.href) && (
                          <button
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.href!); }}
                            className={`opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isPinned(item.href) ? 'opacity-100' : ''} ${
                              item.href === '/' || item.href === '/settings' || item.href === '/system-admin' ? 'ml-auto' : 'ml-1.5'
                            }`}
                            title={isPinned(item.href) ? (t('nav.unpin') || 'Eltávolítás') : (t('nav.pin') || 'Kedvencekhez')}
                          >
                            {isPinned(item.href) ? (
                              <BookmarkSlashIcon className="h-3.5 w-3.5 text-gray-400" />
                            ) : (
                              <BookmarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-amber-500" />
                            )}
                          </button>
                        )}
                      </>
                    )}
                  </Link>
                );
                }

                return null;
              })}
            </div>
          </nav>

          {/* Alsó fix rész - mindig látható */}
          <div className="flex-shrink-0 mt-auto">
            {/* Modern divider */}
            <div className="px-3 mb-4">
              <div className="h-px bg-[var(--crm-border)]"></div>
            </div>
            
            
            {/* Rendszer üzenet - ha van */}
            {systemMessage && !isSidebarCollapsed && (
              <div className="px-3 mb-3">
                <div className="flex items-start space-x-2 p-3 rounded-lg bg-primary-50 dark:bg-primary-500/10 border border-primary-100 dark:border-primary-500/20">
                  <InformationCircleIcon className="w-4 h-4 flex-shrink-0 text-blue-600 mt-0.5" />
                  <span className="text-xs leading-relaxed text-blue-700">{systemMessage}</span>
                </div>
              </div>
            )}
            
            {/* Profil és kijelentkezés */}
            <div className="p-3">
              <Link
                to="/profile"
                className={`group relative flex items-center ${isSidebarCollapsed ? 'justify-center p-2' : 'p-3 space-x-3'} rounded-xl transition-all duration-200 ${
                  isActiveRoute('/profile') 
                    ? 'bg-primary-50 dark:bg-primary-500/15' 
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                title={isSidebarCollapsed ? user?.displayName || user?.email : undefined}
              >
                {/* Active indicator */}
                {isActiveRoute('/profile') && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-primary-600 dark:bg-primary-400 rounded-r-full"></div>
                )}
                
                {user?.photoURL ? (
                  <div className={`${isSidebarCollapsed ? 'h-8 w-8' : 'h-10 w-10'} rounded-xl overflow-hidden flex-shrink-0 ring-2 transition-all duration-200 ${
                    isActiveRoute('/profile') ? 'ring-primary-200' : 'ring-gray-200 group-hover:ring-primary-200'
                  }`}>
                    <img
                      src={user.photoURL}
                      alt={user?.displayName || 'Profile'}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className={`${isSidebarCollapsed ? 'h-8 w-8' : 'h-10 w-10'} rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 ring-2 transition-all duration-200 ${
                    isActiveRoute('/profile') ? 'ring-primary-200' : 'ring-gray-200 group-hover:ring-primary-200'
                  }`}>
                    <span className={`${isSidebarCollapsed ? 'text-xs' : 'text-sm'} font-medium text-white`}>
                      {user?.displayName?.[0] || user?.email?.[0] || '?'}
                    </span>
                  </div>
                )}
                
                {!isSidebarCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium transition-all duration-200 ${
                      isActiveRoute('/profile') ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300 group-hover:text-primary-700 dark:group-hover:text-primary-300'
                    } truncate`}>
                      {user?.displayName}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-300 truncate">
                      {user?.email}
                    </p>
                    <div className="flex items-center space-x-1 mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        user?.role === 'super_admin' ? 'bg-red-400' : 
                        user?.role === 'admin' ? 'bg-orange-400' : 'bg-green-400'
                      }`}></div>
                      <p className="text-xs text-primary-500 font-medium">
                        {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Hover effect */}
                <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
                  isActiveRoute('/profile') ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
                } bg-gray-100 dark:bg-gray-800`}></div>
              </Link>
              
              <div className={`flex items-center gap-1.5 mt-3 ${isSidebarCollapsed ? 'flex-col' : 'flex-wrap justify-center'}`}>
                {/* Collapse/Expand toggle */}
                <button
                  onClick={toggleSidebar}
                  className="group flex items-center justify-center p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={isSidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
                >
                  <div className="relative w-3.5 h-3.5">
                    <div className={`absolute inset-0 transition-all duration-300 ${isSidebarCollapsed ? 'rotate-0 opacity-100' : 'rotate-45 opacity-0'}`}>
                      <Bars3Icon className="w-3.5 h-3.5" />
                    </div>
                    <div className={`absolute inset-0 transition-all duration-300 ${isSidebarCollapsed ? 'rotate-45 opacity-0' : 'rotate-0 opacity-100'}`}>
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </button>
                
                <button
                  onClick={signOut}
                  className="group flex items-center justify-center p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={t('common.logout')}
                >
                  <ArrowRightOnRectangleIcon className="h-3.5 w-3.5 flex-shrink-0 transition-all duration-200 text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400" />
                </button>
                
                <ThemeSelector />
                
                <button
                  onClick={() => handleLanguageChange(language === 'hu' ? 'en' : 'hu')}
                  className="group flex items-center justify-center p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
                >
                  <div className="w-4 h-4 flex items-center justify-center transition-all duration-200">
                    {language === 'hu' ? (
                      <span className="font-bold text-[10px] text-blue-600">EN</span>
                    ) : (
                      <span className="font-bold text-[10px] text-green-600">HU</span>
                    )}
                  </div>
                </button>
                
                <button
                  onClick={toggleFAB}
                  className="group flex items-center justify-center p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title={isFABVisible ? 'FAB elrejtése' : 'FAB megjelenítése'}
                >
                  <div className="w-4 h-4 flex items-center justify-center transition-all duration-200">
                    {isFABVisible ? (
                      <EyeSlashIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
                    ) : (
                      <PlusIcon className="h-3.5 w-3.5 text-primary-500 group-hover:text-primary-600" />
                    )}
                  </div>
                </button>
                
                <NotificationButton />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobil nézet - felső sáv */}
      <div className="h-20 bg-[var(--crm-surface)] border-b border-[var(--crm-border)] fixed top-0 left-0 z-30 w-full block md:hidden">
        <div className="flex items-center justify-between h-full px-6">
          {/* Logo - modernizált */}
          <Link
            to={user?.role === 'customer' ? '/customer-portal' : '/'}
            className="flex items-center justify-center relative group py-3"
          >
            <div className="relative transform transition-transform duration-300">
              <div className="p-1 sm:p-2">
                <img 
                  src={theme === 'dark' ? crmLogoWhite : crmLogoBlack} 
                  alt="YouCRM Logo" 
                  className="h-10 sm:h-12 w-auto transition-transform duration-300" 
                />
              </div>
            </div>
          </Link>

          {/* Jobb oldali elemek - értesítések, profil és hamburger menü */}
          <div className="flex items-center space-x-1">
            {/* Értesítési központ */}
            <NotificationButton />
            
            {/* Profil ikon */}
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={toggleProfileMenu}
                className="flex items-center justify-center p-1 rounded-md text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                {user?.photoURL ? (
                  <div className="h-8 w-8 rounded-full overflow-hidden">
                    <img
                      src={user.photoURL}
                      alt={user?.displayName || 'Profile'}
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm text-primary-600">
                      {user?.displayName?.[0] || user?.email?.[0] || '?'}
                    </span>
                  </div>
                )}
              </button>

              {/* Profil menü */}
              {isProfileMenuOpen && (
                <div className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden z-10 w-60 py-1">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center space-x-3">
                    {user?.photoURL ? (
                      <div className="h-10 w-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-primary-100">
                        <img
                          src={user.photoURL}
                          alt={user?.displayName || 'Profile'}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 border-2 border-primary-50">
                        <span className="text-base font-medium text-primary-600">
                          {user?.displayName?.[0] || user?.email?.[0] || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {user?.displayName || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {user?.email}
                      </p>
                      <p className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                        {user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'admin' ? 'Admin' : 'User'}
                      </p>
                    </div>
                  </div>

                  <div className="px-1 py-2">
                    <Link
                      to="/profile"
                      onClick={() => setIsProfileMenuOpen(false)}
                      className={`flex items-center w-full px-3 py-2.5 text-sm ${
                        isActiveRoute('/profile') ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      } rounded-md`}
                    >
                      <Cog6ToothIcon className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                      {t('nav.profile')}
                    </Link>
                    <button
                      onClick={signOut}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md mt-1"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                      {t('common.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 p-2 ml-1"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 top-20 z-40 bg-white dark:bg-gray-800 overflow-y-auto shadow-md">
            <div className="container mx-auto px-3 py-2">
              {/* Organization Changer - Mobile */}
              {organizations.length > 0 && (
                <div className="mb-3 crm-card overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setIsMobileOrgOpen(v => !v)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
                    aria-expanded={isMobileOrgOpen}
                  >
                    <BuildingOffice2Icon className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{getCurrentOrganization()?.name || t('nav.selectOrganization')}</span>
                      <span className="block text-[11px] text-gray-500 dark:text-gray-400">{t('nav.organization')}</span>
                    </span>
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isMobileOrgOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobileOrgOpen && organizations.length > 1 && (
                  <div className="space-y-0.5 px-2 pb-2 border-t border-[var(--crm-border)] pt-2">
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => {
                          handleOrganizationChange(org.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors ${
                          user?.organizationId === org.id 
                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' 
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                      >
                        <span className="truncate">{org.name}</span>
                        {user?.organizationId === org.id && (
                          <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 ml-2" />
                        )}
                      </button>
                    ))}
                  </div>
                  )}
                </div>
              )}
              
              {/* Kedvencek - Mobile */}
              {pinnedItems.length > 0 && !location.pathname.startsWith('/dashboard') && !location.pathname.startsWith('/modern-dashboard') && (
                <div className="mb-4">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('nav.favorites')}
                  </div>
                  <div className="space-y-1">
                    {flatNavItems
                      .filter(navItem => isPinnable(navItem.href) && pinnedItems.includes(navItem.href))
                      .map(navItem => {
                        const active = isActiveRoute(navItem.href);
                        return (
                          <Link
                            key={`pinned-mobile-${navItem.href}`}
                            to={navItem.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                              active
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className={`h-6 w-6 rounded-md ${active ? 'bg-amber-200 dark:bg-amber-800' : 'bg-gray-100 dark:bg-gray-700'} flex items-center justify-center mr-3`}>
                              <navItem.icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-primary-600 dark:text-primary-300' : 'text-gray-500 dark:text-gray-400'}`} />
                            </div>
                            <span className="flex-1">{t(navItem.name)}</span>
                            <button
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(navItem.href); }}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                              title={t('nav.unpin')}
                            >
                              <BookmarkSlashIcon className="h-3.5 w-3.5 text-gray-400" />
                            </button>
                          </Link>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Mobile Navigation Items */}
              <div className="space-y-2">
                {filteredNavigation.map((item) => {
                  // Handle groups in mobile
                  if (item.isGroup && item.children) {
                    const hasActiveChild = item.children.some(child => child.href && isActiveRoute(child.href));
                    const isExpanded = isGroupExpanded(item.name);
                    
                    return (
                      <div key={item.name} className="space-y-1">
                        {/* Group Header - Mobile */}
                        <button
                          onClick={() => toggleGroup(item.name)}
                          className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-lg ${
                            hasActiveChild
                              ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          } transition-colors`}
                        >
                          <div className={`h-6 w-6 rounded-md ${
                            hasActiveChild ? 'bg-primary-200 dark:bg-primary-800' : 'bg-gray-100 dark:bg-gray-700'
                          } flex items-center justify-center mr-3`}>
                            <item.icon className={`h-4 w-4 flex-shrink-0 ${
                              hasActiveChild ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'
                            }`} />
                          </div>
                          <span className="flex-1 text-left">{t(item.name)}</span>
                          <ChevronRightIcon className={`h-4 w-4 transition-transform duration-200 ${
                            isExpanded ? 'rotate-90' : ''
                          } ${hasActiveChild ? 'text-primary-500' : 'text-gray-400'}`} />
                        </button>

                        {/* Group Children - Mobile */}
                        {isExpanded && (
                          <div className="ml-6 space-y-1">
                            {item.children.map((child) => {
                              if (!child.href) return null;
                              
                              const active = isActiveRoute(child.href);
                              const moduleStatus = getModuleStatus(child);
                              
                              if (child.disabled) {
                                return (
                                  <div
                                    key={child.name}
                                    className="flex items-center px-3 py-2 text-sm font-medium rounded-lg text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                  >
                                    <div className="h-5 w-5 rounded-md bg-gray-100 flex items-center justify-center mr-3">
                                      <child.icon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 dark:text-gray-500" />
                                    </div>
                                    <span>{t(child.name)}</span>
                                  </div>
                                );
                              }
                              
                              return (
                                <Link
                                  key={child.name}
                                  to={child.href}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                                    active
                                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                  } transition-colors group`}
                                >
                                  <div className={`h-5 w-5 rounded-md ${
                                    active ? 'bg-primary-200 dark:bg-primary-800' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-100 dark:group-hover:bg-primary-800'
                                  } flex items-center justify-center mr-3`}>
                                    <child.icon className={`h-3.5 w-3.5 flex-shrink-0 ${
                                      active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                                    }`} />
                                  </div>
                                  <span className="flex-1">{t(child.name)}</span>
                                  
                                  {/* Module status indicator */}
                                  {child.module && moduleStatus !== 'common' && (
                                    <div className="flex-shrink-0 ml-2">
                                      {moduleStatus === 'enabled' && (
                                        <></>
                                      )}
                                      {moduleStatus === 'premium' && (
                                        <div className="w-2 h-2 bg-amber-400 rounded-full" title={t('common.modules.premium')}></div>
                                      )}
                                      {moduleStatus === 'disabled' && (
                                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full shadow-sm" title={t('common.modules.disabled')}></div>
                                      )}
                                    </div>
                                  )}

                                  {/* Pin button - Mobile */}
                                  {child.href && (
                                    <button
                                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(child.href!); }}
                                      className={`ml-2 p-1 rounded ${isPinned(child.href!) ? 'text-amber-500' : 'text-gray-400'}`}
                                      title={isPinned(child.href!) ? t('nav.unpin') : t('nav.pin')}
                                    >
                                      {isPinned(child.href!) ? (
                                        <BookmarkSlashIcon className="h-3.5 w-3.5" />
                                      ) : (
                                        <BookmarkIcon className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  )}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Handle regular items (non-group) in mobile
                  if (!item.isGroup && item.href) {
                  const active = isActiveRoute(item.href);
                  const moduleStatus = getModuleStatus(item);
                  
                  if (item.disabled) {
                    return (
                      <div
                        key={item.name}
                        className="flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-gray-400 dark:text-gray-500 cursor-not-allowed"
                      >
                        <div className="h-6 w-6 rounded-md bg-gray-100 flex items-center justify-center mr-3">
                                                      <item.icon className="h-4 w-4 flex-shrink-0 text-gray-300 dark:text-gray-500" />
                        </div>
                        <span>{t(item.name)}</span>
                      </div>
                    );
                  }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg ${
                        active
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600' 
                      } transition-colors group`}
                    >
                      <div className={`h-6 w-6 rounded-md ${
                        active ? 'bg-primary-200 dark:bg-primary-800' : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-primary-100 dark:group-hover:bg-primary-800'
                      } flex items-center justify-center mr-3`}>
                        <item.icon className={`h-4 w-4 flex-shrink-0 ${
                          active ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                        }`} />
                      </div>
                      <span className="flex-1">{t(item.name)}</span>
                      
                      {item.module && moduleStatus !== 'common' && (
                        <div className="flex-shrink-0 ml-2">
                          {moduleStatus === 'enabled' && (
                            <></>
                          )}
                          {moduleStatus === 'premium' && (
                            <div className="w-2.5 h-2.5 bg-amber-400 rounded-full" title={t('common.modules.premium')}></div>
                          )}
                          {moduleStatus === 'disabled' && (
                            <div className="w-2.5 h-2.5 bg-gray-400 dark:bg-gray-600 rounded-full shadow-sm" title={t('common.modules.disabled')}></div>
                          )}
                        </div>
                      )}

                      {/* Pin button - Mobile */}
                      {isPinnable(item.href) && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(item.href!); }}
                          className={`ml-2 p-1 rounded ${isPinned(item.href) ? 'text-amber-500' : 'text-gray-400'}`}
                          title={isPinned(item.href) ? (t('nav.unpin') || 'Eltávolítás') : (t('nav.pin') || 'Kedvencekhez')}
                        >
                          {isPinned(item.href) ? (
                            <BookmarkSlashIcon className="h-3.5 w-3.5" />
                          ) : (
                            <BookmarkIcon className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </Link>
                  );
                  }

                  return null;
                })}
              </div>

              <div className="mt-3 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={signOut}
                    className="flex items-center w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                                          <div className="h-6 w-6 rounded-md bg-red-50 dark:bg-red-900/20 flex items-center justify-center mr-3">
                        <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0 text-red-500 dark:text-red-400" />
                    </div>
                    {t('common.logout')}
                  </button>
                  
                  <ThemeSelector />
                  
                  <button
                    onClick={() => handleLanguageChange(language === 'hu' ? 'en' : 'hu')}
                    className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                    title={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
                  >
                    <span className="font-medium text-sm">{language === 'hu' ? 'EN' : 'HU'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Portal-based Hover Menu */}
      {hoveredGroup && isSidebarCollapsed && createPortal(
        <div 
          className="fixed z-[99999]"
          style={{
            top: hoverMenuPosition.top,
            left: hoverMenuPosition.left
          }}
          onMouseEnter={handleMenuEnter}
          onMouseLeave={handleMenuLeave}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[200px] ring-1 ring-black/5 dark:ring-white/10">
            {(() => {
              const groupItem = navigation.find(item => item.name === hoveredGroup);
              if (!groupItem || !groupItem.children) return null;

              return (
                <>
                  {/* Group Title */}
                  <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <groupItem.icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{t(groupItem.name)}</span>
                    </div>
                  </div>
                  
                  {/* Group Items */}
                  <div className="py-1">
                    {groupItem.children.map((child) => {
                      if (!child.href) return null;
                      
                      const active = isActiveRoute(child.href);
                      const moduleStatus = getModuleStatus(child);
                      
                      if (child.disabled) {
                        return (
                          <div
                            key={child.name}
                            className="flex items-center px-3 py-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed"
                          >
                            <child.icon className="h-4 w-4 mr-3 text-gray-300 dark:text-gray-500" />
                            <span>{t(child.name)}</span>
                          </div>
                        );
                      }
                      
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={() => {
                            setHoveredGroup(null);
                            if (hoverTimeoutRef.current) {
                              clearTimeout(hoverTimeoutRef.current);
                              hoverTimeoutRef.current = null;
                            }
                          }}
                          className={`flex items-center px-3 py-2 text-sm transition-colors ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          <child.icon className={`h-4 w-4 mr-3 ${
                            active 
                              ? 'text-primary-600 dark:text-primary-400' 
                              : 'text-gray-500 dark:text-gray-400'
                          }`} />
                          <span className="flex-1">{t(child.name)}</span>
                          
                          {/* Module status indicator */}
                          {child.module && moduleStatus !== 'common' && (
                            <div className="ml-2">
                              {moduleStatus === 'enabled' && (
                                <></>
                              )}
                              {moduleStatus === 'premium' && (
                                <div className="w-2 h-2 bg-amber-400 rounded-full" title={t('common.modules.premium')}></div>
                              )}
                              {moduleStatus === 'disabled' && (
                                <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full" title={t('common.modules.disabled')}></div>
                              )}
                            </div>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* Portal-based Organization Selector */}
      {showOrgSelector && isSidebarCollapsed && organizations.length > 0 && createPortal(
        <div 
          className="fixed z-[99999]"
          style={{
            top: orgSelectorPosition.top,
            left: orgSelectorPosition.left
          }}
          onMouseEnter={handleOrgMenuEnter}
          onMouseLeave={handleOrgMenuLeave}
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[250px] ring-1 ring-black/5 dark:ring-white/10">
            {/* Organization Selector Title */}
            <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2">
                <BuildingOffice2Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">{t('nav.organization')}</span>
              </div>
            </div>
            
            {/* Organization List */}
            <div className="py-1 max-h-64 overflow-y-auto">
              {organizations.map((org) => (
                <button
                  key={org.id}
                  onClick={() => {
                    handleOrganizationChange(org.id);
                    setShowOrgSelector(false);
                    if (orgTimeoutRef.current) {
                      clearTimeout(orgTimeoutRef.current);
                      orgTimeoutRef.current = null;
                    }
                  }}
                  className={`w-full flex items-start justify-between px-3 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    user?.organizationId === org.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className={`p-1.5 rounded-lg ${
                      user?.organizationId === org.id 
                        ? 'bg-primary-200/50 dark:bg-primary-800/30' 
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      <BuildingOffice2Icon className={`h-3.5 w-3.5 ${
                        user?.organizationId === org.id 
                          ? 'text-primary-600 dark:text-primary-400' 
                          : 'text-gray-500 dark:text-gray-400'
                      }`} />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <p className={`font-medium break-words leading-tight ${
                        user?.organizationId === org.id 
                          ? 'text-primary-700 dark:text-primary-300' 
                          : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {org.name}
                      </p>
                      {org.taxNumber && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                          {org.taxNumber}
                        </p>
                      )}
                    </div>
                  </div>
                  {user?.organizationId === org.id && (
                    <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-1" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Command Palette */}
      <CommandPalette
        items={flatNavItems}
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
      />
    </>
  );
};

export default NavBar; 
